import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import RadialMetric from './components/RadialMetric';
import { apiFetch, getUserFacingErrorMessage, throwIfNotOk } from './services/apiClient';
import './JobsPage.css';
import './components/ScreeningAutomationPanel.css';

/** Full public application URL for clipboard / new tab */
function fullApplicationUrl(applicationUrl) {
    if (!applicationUrl) return '';
    const baseUrl = window.location.origin;
    if (applicationUrl.startsWith('http')) return applicationUrl;
    return `${baseUrl}${applicationUrl.startsWith('/') ? '' : '/'}${applicationUrl}`;
}

const JobsListPage = () => {
    const navigate = useNavigate();
    const [jobs, setJobs] = useState([]);
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [deletingJobId, setDeletingJobId] = useState(null);
    const [expandedJobId, setExpandedJobId] = useState(null);
    const [jobDetails, setJobDetails] = useState({});
    const fetchingJobIdsRef = useRef(new Set());
    const [recentUpload, setRecentUpload] = useState(null);
    const [recentProcessing, setRecentProcessing] = useState(null);
    const [highlightJobId, setHighlightJobId] = useState(null);

    const [openMenuJobId, setOpenMenuJobId] = useState(null);
    const [menuDirection, setMenuDirection] = useState('down');
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [isClosingUploadModal, setIsClosingUploadModal] = useState(false);
    const [uploadJobId, setUploadJobId] = useState(null);
    const [uploadJobTitle, setUploadJobTitle] = useState('');
    const [uploadFiles, setUploadFiles] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadResult, setUploadResult] = useState(null);
    const fileInputRef = useRef(null);
    const menuRef = useRef(null);
    const uploadModalCloseTimerRef = useRef(null);
    const [viewMode, setViewMode] = useState(() => {
        try {
            return localStorage.getItem('jobs_view_mode') || 'grid';
        } catch (e) {
            return 'grid';
        }
    });
    const [searchQuery, setSearchQuery] = useState('');
    const [pageSize, setPageSize] = useState(() => {
        try {
            const raw = localStorage.getItem('jobs_page_size');
            const parsed = raw ? parseInt(raw, 10) : 12;
            return Number.isFinite(parsed) && parsed > 0 ? parsed : 12;
        } catch (e) {
            return 12;
        }
    });
    const [currentPage, setCurrentPage] = useState(1);

    const clearRecentActivity = () => {
        try { localStorage.removeItem('recent_upload_job'); } catch (e) {}
        setRecentUpload(null);
        setRecentProcessing(null);
        setHighlightJobId(null);
    };

    useEffect(() => {
        fetchJobs();
        
        // Auto-refresh every 10 seconds to show new processed resumes
        const interval = setInterval(() => {
            fetchJobs();
        }, 10000);
        
        return () => clearInterval(interval);
    }, []);

    // Persist view mode
    useEffect(() => {
        try {
            localStorage.setItem('jobs_view_mode', viewMode);
        } catch (e) {
            // ignore
        }
    }, [viewMode]);

    // Persist page size
    useEffect(() => {
        try {
            localStorage.setItem('jobs_page_size', String(pageSize));
        } catch (e) {
            // ignore
        }
    }, [pageSize]);

    // Load recent upload context (for "Recent activity" strip)
    useEffect(() => {
        try {
            const raw = localStorage.getItem('recent_upload_job');
            if (!raw) return;
            const parsed = JSON.parse(raw);
            if (parsed && parsed.job_id) {
                setRecentUpload(parsed);
                setHighlightJobId(parsed.job_id);
                // Remove highlight after a short time
                setTimeout(() => setHighlightJobId(null), 12000);
            }
        } catch (e) {
            // ignore bad localStorage
        }
    }, []);

    // Poll processing status for recent upload job (for the strip)
    useEffect(() => {
        if (!recentUpload?.job_id) return;
        let cancelled = false;
        let intervalId = null;

        const poll = async () => {
            try {
                const res = await fetch(`/api/jobs/${recentUpload.job_id}/processing-status`, { credentials: 'include' });
                // If the job was deleted, clear the recent activity strip gracefully.
                if (res.status === 404) {
                    if (!cancelled) clearRecentActivity();
                    return;
                }
                if (!res.ok) return;
                const data = await res.json();
                if (!cancelled) setRecentProcessing(data);
            } catch (e) {
                // ignore
            }
        };

        poll();
        intervalId = setInterval(poll, 5000);

        return () => {
            cancelled = true;
            if (intervalId) clearInterval(intervalId);
        };
    }, [recentUpload?.job_id]);

    // Fetch job details in background for jobs with resumes to populate metrics
    useEffect(() => {
        if (jobs.length > 0) {
            // Fetch details for jobs that have resumes and analysis, but haven't been fetched yet
            jobs.forEach(job => {
                // Use functional state check to avoid stale closure
                setJobDetails(prevDetails => {
                    // Check if we should fetch this job
                    if (job.resume_count > 0 && 
                        job.has_analysis && 
                        !prevDetails[job.id] && 
                        !fetchingJobIdsRef.current.has(job.id)) {
                        
                        // Mark as fetching
                        fetchingJobIdsRef.current.add(job.id);
                        
                        // Fetch in background without blocking UI
                        fetchJobDetails(job.id)
                            .then(() => {
                                // Remove from fetching set when done
                                fetchingJobIdsRef.current.delete(job.id);
                            })
                            .catch(err => {
                                console.error(`Failed to fetch metrics for job ${job.id}:`, err);
                                // Remove from fetching set on error too
                                fetchingJobIdsRef.current.delete(job.id);
                            });
                    }
                    return prevDetails; // Return unchanged state
                });
            });
        }
    }, [jobs]); // Fetch when jobs list changes

    const fetchJobs = async () => {
        try {
            const response = await apiFetch('/api/jobs');
            await throwIfNotOk(response);
            const data = await response.json();
            setJobs(data);
            // If the job referenced by "Recent activity" no longer exists (deleted),
            // clear it gracefully so the UI doesn't point to a broken page.
            if (recentUpload?.job_id && Array.isArray(data) && !data.some(j => j?.id === recentUpload.job_id)) {
                clearRecentActivity();
            }
        } catch (error) {
            setError(error.message);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchJobDetails = async (jobId) => {
        // If already cached, don't fetch again
        if (jobDetails[jobId]) {
            return jobDetails[jobId];
        }

        try {
            const response = await apiFetch(`/api/jobs/${jobId}`);
            await throwIfNotOk(response);
            const data = await response.json();
            setJobDetails(prev => ({ ...prev, [jobId]: data }));
            return data;
        } catch (error) {
            console.error(`Error fetching job details for ${jobId}:`, error);
            return null;
        }
    };

    const handleCardClick = async (jobId) => {
        if (expandedJobId === jobId) {
            setExpandedJobId(null);
        } else {
            setExpandedJobId(jobId);
            // Fetch job details if not already cached
            if (!jobDetails[jobId]) {
                await fetchJobDetails(jobId);
            }
        }
    };

    const handleDeleteJob = async (jobId, jobDescription, e) => {
        e.stopPropagation(); // Prevent card expansion
        const confirmMessage = `Are you sure you want to delete this job?\n\n"${jobDescription.substring(0, 100)}..."\n\nThis will permanently delete the job and all ${jobs.find(j => j.id === jobId)?.resume_count || 0} associated resumes. This action cannot be undone.`;
        
        if (!window.confirm(confirmMessage)) {
            return;
        }

        setDeletingJobId(jobId);
        
        try {
            const response = await apiFetch(`/api/jobs/${jobId}`, {
                method: 'DELETE',
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(getUserFacingErrorMessage(response.status, errorData, 'Failed to delete job'));
            }
            
            // If the deleted job is the "recent activity" one, clear it gracefully.
            if (recentUpload?.job_id === jobId) {
                clearRecentActivity();
            }

            // Refresh the jobs list
            await fetchJobs();
            // Remove from job details cache
            setJobDetails(prev => {
                const newDetails = { ...prev };
                delete newDetails[jobId];
                return newDetails;
            });
            
        } catch (error) {
            setError(`Failed to delete job: ${error.message}`);
        } finally {
            setDeletingJobId(null);
        }
    };

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setOpenMenuJobId(null);
            }
        };
        if (openMenuJobId !== null) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [openMenuJobId]);

    const openAddResumeModal = useCallback((jobId, description) => {
        if (uploadModalCloseTimerRef.current) {
            clearTimeout(uploadModalCloseTimerRef.current);
            uploadModalCloseTimerRef.current = null;
        }
        setOpenMenuJobId(null);
        setIsClosingUploadModal(false);
        setUploadJobId(jobId);
        setUploadJobTitle(extractJobTitle(description));
        setUploadFiles([]);
        setUploadResult(null);
        setShowUploadModal(true);
    }, []);

    const closeUploadModal = useCallback(() => {
        if (isClosingUploadModal || !showUploadModal) return;
        setIsClosingUploadModal(true);
        if (uploadModalCloseTimerRef.current) clearTimeout(uploadModalCloseTimerRef.current);
        uploadModalCloseTimerRef.current = setTimeout(() => {
            setShowUploadModal(false);
            setIsClosingUploadModal(false);
            setUploadJobId(null);
            setUploadJobTitle('');
            setUploadFiles([]);
            setUploadResult(null);
            uploadModalCloseTimerRef.current = null;
        }, 220);
    }, [isClosingUploadModal, showUploadModal]);

    useEffect(() => {
        return () => {
            if (uploadModalCloseTimerRef.current) {
                clearTimeout(uploadModalCloseTimerRef.current);
                uploadModalCloseTimerRef.current = null;
            }
        };
    }, []);

    const handleFileSelect = useCallback((e) => {
        const files = Array.from(e.target.files || []);
        setUploadFiles(prev => [...prev, ...files]);
        if (fileInputRef.current) fileInputRef.current.value = '';
    }, []);

    const removeFile = useCallback((index) => {
        setUploadFiles(prev => prev.filter((_, i) => i !== index));
    }, []);

    const handleUploadResumes = useCallback(async () => {
        if (!uploadJobId || uploadFiles.length === 0) return;
        setIsUploading(true);
        setUploadResult(null);
        try {
            const formData = new FormData();
            uploadFiles.forEach(f => formData.append('resumes', f));
            const res = await apiFetch(`/api/jobs/${uploadJobId}/add-resumes`, {
                method: 'POST',
                body: formData,
            });
            const data = await res.json();
            if (!res.ok) {
                setUploadResult({ success: false, message: data.detail || 'Upload failed' });
            } else {
                setUploadResult({ success: true, message: data.message });
                await fetchJobs();
                setJobDetails(prev => {
                    const updated = { ...prev };
                    delete updated[uploadJobId];
                    return updated;
                });
                fetchingJobIdsRef.current.delete(uploadJobId);
                closeUploadModal();
            }
        } catch (err) {
            setUploadResult({ success: false, message: err.message || 'Upload failed' });
        } finally {
            setIsUploading(false);
        }
    }, [uploadJobId, uploadFiles, fetchJobs, closeUploadModal]);

    // Function to extract job title from description
    const extractJobTitle = (description) => {
        if (!description) return 'Untitled Job';
        
        const filePrefixMatch = description.match(/^File:\s*([^\n]+)\n/);
        if (filePrefixMatch) {
            const filename = filePrefixMatch[1];
            const filenameWithoutExt = filename.replace(/\.[^.]*$/, '');
            return filenameWithoutExt;
        }
        
        const lines = description.split('\n').filter(line => line.trim().length > 0);
        if (lines.length > 0) {
            const firstLine = lines[0].trim();
            const cleaned = firstLine
                .replace(/^Job description from file:\s*/i, '')
                .replace(/^Job description from files:\s*/i, '')
                .trim();
            
            if (cleaned.length > 100) {
                const breakPoint = cleaned.substring(0, 100).lastIndexOf(',') || 
                                 cleaned.substring(0, 100).lastIndexOf(' ') || 
                                 100;
                return cleaned.substring(0, breakPoint).trim() + '...';
            }
            
            return cleaned || 'Untitled Job';
        }
        
        return truncateText(description, 100);
    };

    const truncateText = (text, length) => {
        if (!text || text.length <= length) return text;
        return text.substring(0, length) + '...';
    };

    // Calculate metrics from job details
    const calculateMetrics = (jobData) => {
        if (!jobData || !jobData.resumes || jobData.resumes.length === 0) {
            return {
                avgFit: null,
                topScore: null,
                eligibleCount: 0,
                blockedCount: 0
            };
        }

        const resumes = jobData.resumes;
        let totalFit = 0;
        let fitCount = 0;
        let topScore = 0;
        let eligibleCount = 0;
        let blockedCount = 0;

        resumes.forEach(resume => {
            // Try to get fit score from RAG analysis first, then legacy analysis
            let fitScore = null;
            
            if (resume.rag_analysis?.evaluation_result?.calibrated_score) {
                fitScore = resume.rag_analysis.evaluation_result.calibrated_score;
            } else if (resume.rag_analysis?.evaluation_result?.dual_scores?.fit_score) {
                fitScore = resume.rag_analysis.evaluation_result.dual_scores.fit_score;
            } else if (resume.analysis?.fit_score) {
                fitScore = resume.analysis.fit_score;
            } else if (resume.analysis?.score) {
                fitScore = resume.analysis.score;
            }

            if (fitScore !== null) {
                totalFit += fitScore;
                fitCount++;
                if (fitScore > topScore) {
                    topScore = fitScore;
                }
            }

            // Check eligibility status
            if (resume.rag_analysis?.evaluation_result?.eligibility_status) {
                const status = resume.rag_analysis.evaluation_result.eligibility_status;
                if (status === 'eligible') {
                    eligibleCount++;
                } else if (status === 'ineligible' || status === 'blocked') {
                    blockedCount++;
                }
            } else if (resume.analysis?.bucket) {
                // Legacy bucket system
                const bucket = resume.analysis.bucket.toLowerCase();
                if (bucket.includes('top-tier') || bucket.includes('strong-fit') || bucket.includes('good-fit')) {
                    eligibleCount++;
                } else if (bucket.includes('not-a-fit') || bucket.includes('lacks-experience')) {
                    blockedCount++;
                }
            }
        });

        return {
            avgFit: fitCount > 0 ? Math.round(totalFit / fitCount) : null,
            topScore: topScore > 0 ? Math.round(topScore) : null,
            eligibleCount,
            blockedCount
        };
    };

    // Get top candidates for preview
    const getTopCandidates = (jobData, limit = 3) => {
        if (!jobData || !jobData.resumes || jobData.resumes.length === 0) {
            return [];
        }

        const candidates = jobData.resumes.map(resume => {
            let fitScore = null;
            let status = 'unknown';

            // Try to get fit score
            if (resume.rag_analysis?.evaluation_result?.calibrated_score) {
                fitScore = resume.rag_analysis.evaluation_result.calibrated_score;
            } else if (resume.rag_analysis?.evaluation_result?.dual_scores?.fit_score) {
                fitScore = resume.rag_analysis.evaluation_result.dual_scores.fit_score;
            } else if (resume.analysis?.fit_score) {
                fitScore = resume.analysis.fit_score;
            } else if (resume.analysis?.score) {
                fitScore = resume.analysis.score;
            }

            // Get status
            if (resume.rag_analysis?.evaluation_result?.eligibility_status) {
                status = resume.rag_analysis.evaluation_result.eligibility_status;
            } else if (resume.analysis?.bucket) {
                const bucket = resume.analysis.bucket.toLowerCase();
                if (bucket.includes('top-tier') || bucket.includes('strong-fit') || bucket.includes('good-fit')) {
                    status = 'eligible';
                } else if (bucket.includes('not-a-fit') || bucket.includes('lacks-experience')) {
                    status = 'ineligible';
                } else {
                    status = 'needs_review';
                }
            }

            return {
                id: resume.id,
                name: resume.candidate_name || resume.filename || 'Unknown Candidate',
                score: fitScore,
                status: status
            };
        });

        // Sort by score (descending) and return top N
        return candidates
            .filter(c => c.score !== null)
            .sort((a, b) => (b.score || 0) - (a.score || 0))
            .slice(0, limit);
    };

    const getJobStatus = (job) => {
        const pending = Number(job.processing_pending);
        if (!Number.isNaN(pending) && pending > 0) {
            return { label: 'Analyzing', color: 'yellow', icon: '🟡' };
        }
        if (job.resume_count === 0) {
            return { label: 'Analyzing', color: 'yellow', icon: '🟡' };
        }
        if (!job.has_analysis) {
            return { label: 'Analyzing', color: 'yellow', icon: '🟡' };
        }

        if (job.analyzed_count !== undefined && job.analyzed_count < job.resume_count) {
            return { label: 'Analyzing', color: 'yellow', icon: '🟡' };
        }

        const jobData = jobDetails[job.id];
        if (jobData) {
            const resumes = jobData.resumes || [];
            const scoredCount = resumes.filter(r =>
                r.rag_analysis?.evaluation_result?.calibrated_score != null ||
                r.analysis?.fit_score != null
            ).length;

            if (resumes.length > 0 && scoredCount === 0) {
                return { label: 'Analyzing', color: 'yellow', icon: '🟡' };
            }

            const metrics = calculateMetrics(jobData);
            if (metrics.eligibleCount > 0) {
                return { label: 'Ready', color: 'green', icon: '🟢' };
            }
            if (metrics.blockedCount > 0 && metrics.eligibleCount === 0) {
                return { label: 'Needs Attention', color: 'red', icon: '🔴' };
            }
        }
        
        return { label: 'Ready', color: 'green', icon: '🟢' };
    };

    const filteredJobs = jobs.filter(job => {
        const q = (searchQuery || '').trim().toLowerCase();
        if (!q) return true;
        const title = extractJobTitle(job.description).toLowerCase();
        const desc = (job.description || '').toLowerCase();
        return title.includes(q) || desc.includes(q);
    });

    // Reset to first page when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery, viewMode, pageSize]);

    const totalPages = Math.max(1, Math.ceil(filteredJobs.length / pageSize));
    const safePage = Math.min(Math.max(currentPage, 1), totalPages);
    const pageStart = (safePage - 1) * pageSize;
    const paginatedJobs = filteredJobs.slice(pageStart, pageStart + pageSize);

    const getPageItems = () => {
        // Compact pagination: 1 … 4 5 6 … N
        const pages = [];
        const add = (p) => pages.push(p);
        const addEllipsis = () => pages.push('…');

        if (totalPages <= 7) {
            for (let p = 1; p <= totalPages; p++) add(p);
            return pages;
        }

        add(1);
        const left = Math.max(2, safePage - 1);
        const right = Math.min(totalPages - 1, safePage + 1);

        if (left > 2) addEllipsis();
        for (let p = left; p <= right; p++) add(p);
        if (right < totalPages - 1) addEllipsis();
        add(totalPages);
        return pages;
    };

    return (
        <div className="jobs-page-container">
            <div className="jobs-page-header">
                <div className="jobs-header-top">
                    <h2 className="jobs-page-title">Active Job Roles</h2>
                </div>
                <div className="jobs-header-divider"></div>
                {!isLoading && !error && jobs.length > 0 && (
                    <p className="jobs-page-subtitle">Showing {jobs.length} active {jobs.length === 1 ? 'role' : 'roles'}</p>
                )}
            </div>

            {recentUpload?.job_id && (
                <div className="recent-activity-strip" role="status" aria-live="polite">
                    <div className="recent-activity-main">
                        <div className="recent-activity-title">Recent activity</div>
                        <div className="recent-activity-subtitle">
                            {(() => {
                                const job = jobs.find(j => j.id === recentUpload.job_id);
                                const title = job ? extractJobTitle(job.description) : `Job #${recentUpload.job_id}`;
                                if (recentProcessing?.total) {
                                    const label = recentProcessing.complete ? 'Completed' : 'Processing';
                                    return `${title} — ${label} ${recentProcessing.processed}/${recentProcessing.total}`;
                                }
                                return `${title} — Processing started`;
                            })()}
                        </div>
                    </div>
                    <div className="recent-activity-actions">
                        <button
                            type="button"
                            className="recent-activity-btn primary"
                            onClick={() => navigate(`/jobs/${recentUpload.job_id}/candidates`)}
                        >
                            View job
                        </button>
                        <button
                            type="button"
                            className="recent-activity-btn"
                            onClick={() => navigate(`/upload`)}
                        >
                            Upload another
                        </button>
                        <button
                            type="button"
                            className="recent-activity-btn"
                            onClick={() => {
                                try { localStorage.removeItem('recent_upload_job'); } catch (e) {}
                                setRecentUpload(null);
                                setRecentProcessing(null);
                            }}
                            aria-label="Dismiss recent activity"
                            title="Dismiss"
                        >
                            Dismiss
                        </button>
                    </div>
                </div>
            )}

            {!isLoading && !error && (
                <div className="jobs-toolbar">
                    <div className="jobs-toolbar-left">
                        <input
                            type="text"
                            className="jobs-search"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Search jobs…"
                            aria-label="Search jobs"
                        />
                        {searchQuery.trim() ? (
                            <div className="jobs-toolbar-meta">
                                Showing {filteredJobs.length} of {jobs.length}
                            </div>
                        ) : null}
                    </div>
                    <div className="jobs-toolbar-right">
                        {!isLoading && !error && (
                            <Link
                                to="/jobs/create"
                                className="create-job-button create-job-button--toolbar"
                            >
                                <span className="create-job-button-icon" aria-hidden="true">+</span>
                                Create job posting
                            </Link>
                        )}
                        <div className="jobs-page-size">
                            <span className="jobs-page-size-label">Per page</span>
                            <select
                                className="jobs-page-size-select"
                                value={pageSize}
                                onChange={(e) => setPageSize(parseInt(e.target.value, 10))}
                                aria-label="Jobs per page"
                            >
                                <option value={6}>6</option>
                                <option value={12}>12</option>
                                <option value={24}>24</option>
                                <option value={48}>48</option>
                            </select>
                        </div>
                        <div className="jobs-view-toggle" role="group" aria-label="Jobs view mode">
                            <button
                                type="button"
                                className={`view-toggle-btn ${viewMode === 'grid' ? 'active' : ''}`}
                                onClick={() => setViewMode('grid')}
                            >
                                Grid
                            </button>
                            <button
                                type="button"
                                className={`view-toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
                                onClick={() => setViewMode('list')}
                            >
                                List
                            </button>
                        </div>
                    </div>
                </div>
            )}
            
            {isLoading && <p className="loading-message">Loading jobs...</p>}
            {error && (
                <p className="message error" role="alert">
                    {error}
                </p>
            )}
            
            {!isLoading && !error && (
                <>
                    {viewMode === 'list' ? (
                        <div className="jobs-list">
                            <div className="jobs-list-header">
                                <div className="jobs-col jobs-col-title">Role</div>
                                <div className="jobs-col jobs-col-status">Status</div>
                                <div className="jobs-col jobs-col-candidates">Candidates</div>
                                <div className="jobs-col jobs-col-metric">Avg fit</div>
                                <div className="jobs-col jobs-col-metric">Top</div>
                                <div className="jobs-col jobs-col-actions">Actions</div>
                            </div>

                            {paginatedJobs.length > 0 ? (
                                paginatedJobs.map(job => {
                                    const jobTitle = extractJobTitle(job.description);
                                    const status = getJobStatus(job);
                                    const jobData = jobDetails[job.id];
                                    const metrics = jobData ? calculateMetrics(jobData) : null;

                                    return (
                                        <div
                                            key={job.id}
                                            className={`job-row ${highlightJobId === job.id ? 'job-row-highlight' : ''}`}
                                            role="button"
                                            tabIndex={0}
                                            onClick={() => navigate(`/jobs/${job.id}/candidates`)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    navigate(`/jobs/${job.id}/candidates`);
                                                }
                                            }}
                                        >
                                            <div className="jobs-col jobs-col-title">
                                                <div className="job-row-title" title={jobTitle}>{jobTitle}</div>
                                                {job.application_url ? (
                                                    <div className="job-row-subtitle">LinkedIn / Application form</div>
                                                ) : null}
                                            </div>
                                            <div className="jobs-col jobs-col-status">
                                                <span className={`status-badge-new status-${status.color}`}>
                                                    <span className="status-icon">{status.icon}</span>
                                                    {status.label}
                                                </span>
                                            </div>
                                            <div className="jobs-col jobs-col-candidates">
                                                {job.resume_count}
                                            </div>
                                            <div className="jobs-col jobs-col-metric">
                                                {metrics?.avgFit ?? '—'}
                                            </div>
                                            <div className="jobs-col jobs-col-metric">
                                                {metrics?.topScore ?? '—'}
                                            </div>
                                            <div className="jobs-col jobs-col-actions">
                                                <button
                                                    type="button"
                                                    className="job-row-action primary"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigate(`/jobs/${job.id}/candidates`);
                                                    }}
                                                >
                                                    View
                                                </button>
                                                <button
                                                    type="button"
                                                    className="job-row-action danger"
                                                    disabled={deletingJobId === job.id}
                                                    onClick={(e) => handleDeleteJob(job.id, job.description, e)}
                                                    title="Delete job"
                                                >
                                                    {deletingJobId === job.id ? 'Deleting…' : 'Delete'}
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })
                            ) : (
                                <div className="jobs-list-empty">
                                    No jobs match your search.
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="jobs-grid">
                            {paginatedJobs.length > 0 ? (
                                paginatedJobs.map(job => {
                            const jobTitle = extractJobTitle(job.description);
                            const isExpanded = expandedJobId === job.id;
                            const jobData = jobDetails[job.id];
                            const metrics = jobData ? calculateMetrics(jobData) : null;
                            const topCandidates = jobData ? getTopCandidates(jobData, 3) : [];
                            const status = getJobStatus(job);

                            return (
                                <div 
                                    key={job.id} 
                                    className={`job-card ${isExpanded ? 'expanded' : ''} ${highlightJobId === job.id ? 'job-card-highlight' : ''} ${openMenuJobId === job.id ? 'has-open-menu' : ''}`}
                                    onClick={() => handleCardClick(job.id)}
                                >
                                    {/* Header Section - Redesigned */}
                                    <div className="job-card-header-new">
                                        <div className="job-title-section">
                                            <h3 className="job-title-new" title={jobTitle}>{jobTitle}</h3>
                                            {job.application_url && (
                                                <span 
                                                    className="linkedin-badge" 
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        // Use stored application_url (secure /apply/t/{slug}); fallback for legacy jobs
                                                        const baseUrl = window.location.origin;
                                                        const toCopy = job.application_url?.startsWith('http')
                                                            ? job.application_url
                                                            : (job.application_url ? `${baseUrl}${job.application_url.startsWith('/') ? '' : '/'}${job.application_url}` : `${baseUrl}/apply/${job.id}`);
                                                        navigator.clipboard.writeText(toCopy);
                                                        alert(`Application URL copied to clipboard!\n\n${toCopy}\n\nYou can paste this URL when creating your LinkedIn job posting.`);
                                                    }}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter' || e.key === ' ') {
                                                            e.preventDefault();
                                                            e.currentTarget.click();
                                                        }
                                                    }}
                                                    role="button"
                                                    tabIndex={0}
                                                    title="Click to copy application URL"
                                                >
                                                    🔗 LinkedIn
                                                </span>
                                            )}
                                        </div>
                                        <div className="job-status-section">
                                            <div className="job-status-left">
                                                <span className={`status-badge-new status-${status.color}`}>
                                                    <span className="status-icon">{status.icon}</span>
                                                    {status.label}
                                                </span>
                                                <button
                                                    type="button"
                                                    className={`screening-summary-chip${job.screening_auto_reject_enabled === false ? ' is-off' : ''}`}
                                                    title="Edit AI screening automation for this job"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigate(`/jobs/${job.id}`, {
                                                            state: { focusScreening: true },
                                                        });
                                                    }}
                                                >
                                                    {job.screening_auto_reject_enabled === false
                                                        ? 'Auto-reject off'
                                                        : `Auto-reject < ${job.screening_reject_below_score ?? 35}`}
                                                </button>
                                            </div>
                                            <span className="resume-count-new">{job.resume_count} {job.resume_count === 1 ? 'Candidate' : 'Candidates'}</span>
                                        </div>
                                    </div>

                                    {/* ZONE 3: Metrics Row - Radial Progress Indicators, fixed height */}
                                    <div className="job-metrics-row-radial">
                                        {metrics ? (
                                            <>
                                                <div className="radial-metric-item">
                                                    <RadialMetric
                                                        value={metrics.avgFit}
                                                        label="Avg fit"
                                                        type="avg-fit"
                                                        maxValue={100}
                                                    />
                                                </div>
                                                <div className="radial-metric-item">
                                                    <RadialMetric
                                                        value={metrics.topScore}
                                                        label="Top"
                                                        type="top"
                                                        maxValue={100}
                                                    />
                                                </div>
                                                <div className="radial-metric-item">
                                                    <RadialMetric
                                                        value={metrics.eligibleCount}
                                                        label="Eligible"
                                                        type="eligible"
                                                        totalCandidates={job.resume_count}
                                                    />
                                                </div>
                                                <div className="radial-metric-item">
                                                    <RadialMetric
                                                        value={metrics.blockedCount}
                                                        label="Blocked"
                                                        type="blocked"
                                                        totalCandidates={job.resume_count}
                                                    />
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                {/* Show placeholder metrics when loading or no data */}
                                                <div className="radial-metric-item">
                                                    <RadialMetric
                                                        value={null}
                                                        label="Avg fit"
                                                        type="avg-fit"
                                                        maxValue={100}
                                                    />
                                                </div>
                                                <div className="radial-metric-item">
                                                    <RadialMetric
                                                        value={null}
                                                        label="Top"
                                                        type="top"
                                                        maxValue={100}
                                                    />
                                                </div>
                                                <div className="radial-metric-item">
                                                    <RadialMetric
                                                        value={0}
                                                        label="Eligible"
                                                        type="eligible"
                                                        totalCandidates={job.resume_count || 0}
                                                    />
                                                </div>
                                                <div className="radial-metric-item">
                                                    <RadialMetric
                                                        value={0}
                                                        label="Blocked"
                                                        type="blocked"
                                                        totalCandidates={job.resume_count || 0}
                                                    />
                                                </div>
                                            </>
                                        )}
                                    </div>

                                    {/* Expanded Content */}
                                    {isExpanded && (
                                        <div className="job-card-expanded">
                                            {job.resume_count === 0 ? (
                                                <div className="awaiting-candidates">
                                                    <p>Awaiting candidates...</p>
                                                    <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginTop: '1rem' }}>
                                                        <Link to={`/jobs/${job.id}`} className="edit-jd-button" onClick={(e) => e.stopPropagation()}>
                                                            Edit Job Description
                                                        </Link>
                                                        <Link 
                                                            to={
                                                                job.application_url
                                                                    ? `/jobs/${job.id}/setup-form`
                                                                    : {
                                                                          pathname: `/jobs/${job.id}/create-posting`,
                                                                          state: {
                                                                              prefilledDescription:
                                                                                  job.description ?? '',
                                                                          },
                                                                      }
                                                            }
                                                            className="edit-jd-button"
                                                            onClick={(e) => e.stopPropagation()}
                                                            style={{ 
                                                                background: '#667eea', 
                                                                color: 'white',
                                                                border: 'none'
                                                            }}
                                                        >
                                                            {job.application_url
                                                                ? '📝 Edit application posting'
                                                                : '🔗 Create application posting'}
                                                        </Link>
                                                    </div>
                                                </div>
                                            ) : topCandidates.length > 0 ? (
                                                <>
                                                    <div className="candidate-preview-new">
                                                        <h4 className="preview-title-new">TOP CANDIDATES</h4>
                                                        <div className="candidate-preview-list-new">
                                                            {topCandidates.map((candidate, idx) => (
                                                                <div key={candidate.id || idx} className="candidate-preview-item-new">
                                                                    <span className="candidate-rank-preview">{idx + 1}</span>
                                                                    <span className="candidate-name-new">{candidate.name}</span>
                                                                    <span className="candidate-score-new">{Math.round(candidate.score)}</span>
                                                                    <span className={`candidate-status-new status-${candidate.status === 'eligible' ? 'eligible' : candidate.status === 'ineligible' ? 'blocked' : 'review'}`}>
                                                                        {candidate.status === 'eligible' ? '✓ Eligible' : 
                                                                         candidate.status === 'ineligible' ? '✗ Blocked' : 
                                                                         '⚠ Review'}
                                                                    </span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                    <div className="expanded-actions-new" style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                                                        <Link 
                                                            to={`/jobs/${job.id}/candidates`} 
                                                            className="view-all-candidates-button"
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            View All {job.resume_count} Candidates →
                                                        </Link>
                                                        {job.application_url && (
                                                            <div style={{ 
                                                                padding: '1rem', 
                                                                background: '#f0f7ff', 
                                                                borderRadius: '8px',
                                                                border: '1px solid #667eea',
                                                                flex: '1 1 100%',
                                                                marginTop: '0.5rem'
                                                            }}>
                                                                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: '#333' }}>
                                                                    Application URL:
                                                                </label>
                                                                <div style={{ display: 'flex', gap: '0.5rem' }}>
                                                                    <input 
                                                                        type="text" 
                                                                        value={job.application_url} 
                                                                        readOnly 
                                                                        style={{ 
                                                                            flex: 1, 
                                                                            padding: '0.5rem', 
                                                                            border: '1px solid #ddd', 
                                                                            borderRadius: '4px',
                                                                            fontSize: '0.9rem'
                                                                        }} 
                                                                    />
                                                                    <button 
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            navigator.clipboard.writeText(
                                                                                fullApplicationUrl(job.application_url)
                                                                            );
                                                                            alert('Application URL copied to clipboard!');
                                                                        }}
                                                                        style={{
                                                                            padding: '0.5rem 1rem',
                                                                            background: '#667eea',
                                                                            color: 'white',
                                                                            border: 'none',
                                                                            borderRadius: '4px',
                                                                            cursor: 'pointer',
                                                                            fontWeight: 600
                                                                        }}
                                                                    >
                                                                        Copy
                                                                    </button>
                                                                </div>
                                                                <small style={{ display: 'block', marginTop: '0.5rem', color: '#666' }}>
                                                                    Copy this URL and use it when posting on LinkedIn or other platforms
                                                                </small>
                                                            </div>
                                                        )}
                                                        {!job.application_url && (
                                                            <Link 
                                                                to={{
                                                                    pathname: `/jobs/${job.id}/create-posting`,
                                                                    state: {
                                                                        prefilledDescription:
                                                                            job.description ?? '',
                                                                    },
                                                                }}
                                                                className="view-all-candidates-button"
                                                                onClick={(e) => e.stopPropagation()}
                                                                style={{ 
                                                                    background: '#667eea', 
                                                                    color: 'white',
                                                                    border: 'none'
                                                                }}
                                                            >
                                                                🔗 Create application posting →
                                                            </Link>
                                                        )}
                                                    </div>
                                                </>
                                            ) : (
                                                <div className="no-candidates-data">
                                                    <p>No candidate data available yet.</p>
                                                    <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                                                        <Link to={`/jobs/${job.id}/candidates`} className="view-candidates-button" onClick={(e) => e.stopPropagation()}>
                                                            View Candidates →
                                                        </Link>
                                                        {job.application_url ? (
                                                            <Link
                                                                to={`/jobs/${job.id}/setup-form`}
                                                                className="view-candidates-button"
                                                                onClick={(e) => e.stopPropagation()}
                                                                style={{
                                                                    background: '#667eea',
                                                                    color: 'white',
                                                                    border: 'none',
                                                                }}
                                                            >
                                                                📝 Edit application posting →
                                                            </Link>
                                                        ) : (
                                                            <Link
                                                                to={{
                                                                    pathname: `/jobs/${job.id}/create-posting`,
                                                                    state: {
                                                                        prefilledDescription:
                                                                            job.description ?? '',
                                                                    },
                                                                }}
                                                                className="view-candidates-button"
                                                                onClick={(e) => e.stopPropagation()}
                                                                style={{
                                                                    background: '#667eea',
                                                                    color: 'white',
                                                                    border: 'none',
                                                                }}
                                                            >
                                                                🔗 Create application posting →
                                                            </Link>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Actions Section - Redesigned */}
                                    {!isExpanded && (
                                        <div className="job-card-actions-new" onClick={(e) => e.stopPropagation()}>
                                            <div className="job-card-primary-action">
                                                <Link 
                                                    to={`/jobs/${job.id}/candidates`} 
                                                    className="view-candidates-button-new"
                                                >
                                                    {job.resume_count === 0 ? 'View Status' : `View All ${job.resume_count} ${job.resume_count === 1 ? 'Candidate' : 'Candidates'} →`}
                                                </Link>
                                            </div>
                                            <div className="job-card-secondary-actions">
                                                <div
                                                    className={`job-card-menu-trigger${openMenuJobId === job.id ? ' is-open' : ''}`}
                                                    ref={openMenuJobId === job.id ? menuRef : null}
                                                >
                                                <button
                                                    type="button"
                                                    className="menu-button-new"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        const willOpen = openMenuJobId !== job.id;
                                                        if (willOpen) {
                                                            const rect = e.currentTarget.getBoundingClientRect();
                                                            const spaceBelow = window.innerHeight - rect.bottom - 80;
                                                            setMenuDirection(spaceBelow < 120 ? 'up' : 'down');
                                                        }
                                                        setOpenMenuJobId(willOpen ? job.id : null);
                                                    }}
                                                    title="More options"
                                                    aria-label="More options"
                                                    aria-expanded={openMenuJobId === job.id}
                                                    aria-haspopup="menu"
                                                >
                                                    <span className="kebab-icon" aria-hidden="true">
                                                        <span className="kebab-dot" />
                                                        <span className="kebab-dot" />
                                                        <span className="kebab-dot" />
                                                    </span>
                                                </button>
                                                {openMenuJobId === job.id && (
                                                    <div
                                                        className={`job-card-dropdown-menu ${menuDirection === 'up' ? 'drop-up' : ''}`}
                                                        role="menu"
                                                    >
                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setOpenMenuJobId(null);
                                                                if (job.application_url) {
                                                                    navigate(
                                                                        `/jobs/${job.id}/setup-form`
                                                                    );
                                                                } else {
                                                                    navigate(
                                                                        `/jobs/${job.id}/create-posting`,
                                                                        {
                                                                            state: {
                                                                                prefilledDescription:
                                                                                    job.description ??
                                                                                    '',
                                                                            },
                                                                        }
                                                                    );
                                                                }
                                                            }}
                                                        >
                                                            <span className="dropdown-icon">
                                                                {job.application_url
                                                                    ? '📝'
                                                                    : '🔗'}
                                                            </span>
                                                            {job.application_url
                                                                ? 'Edit application posting'
                                                                : 'Create application posting'}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setOpenMenuJobId(null);
                                                                openAddResumeModal(
                                                                    job.id,
                                                                    job.description
                                                                );
                                                            }}
                                                        >
                                                            <span className="dropdown-icon">📄</span>
                                                            Add Resume
                                                        </button>
                                                    </div>
                                                )}
                                                </div>
                                                <button
                                                    type="button"
                                                    className="delete-button-new"
                                                    onClick={(e) => handleDeleteJob(job.id, job.description, e)}
                                                    disabled={deletingJobId === job.id}
                                                    title="Delete job"
                                                >
                                                    {deletingJobId === job.id ? 'Deleting...' : '🗑️'}
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                                })
                            ) : (
                                <div className="no-jobs-message">
                                    <p>No jobs submitted yet.</p>
                                    <Link to="/upload" className="upload-link">Upload one now!</Link>
                                </div>
                            )}
                        </div>
                    )}

                    {filteredJobs.length > 0 && totalPages > 1 && (
                        <div className="jobs-pagination" role="navigation" aria-label="Jobs pagination">
                            <button
                                type="button"
                                className="jobs-page-btn"
                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                disabled={safePage <= 1}
                            >
                                Prev
                            </button>

                            <div className="jobs-page-numbers" aria-label="Page numbers">
                                {getPageItems().map((item, idx) => (
                                    item === '…' ? (
                                        <span key={`e-${idx}`} className="jobs-page-ellipsis">…</span>
                                    ) : (
                                        <button
                                            key={`p-${item}`}
                                            type="button"
                                            className={`jobs-page-number ${item === safePage ? 'active' : ''}`}
                                            onClick={() => setCurrentPage(item)}
                                        >
                                            {item}
                                        </button>
                                    )
                                ))}
                            </div>

                            <button
                                type="button"
                                className="jobs-page-btn"
                                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                disabled={safePage >= totalPages}
                            >
                                Next
                            </button>
                        </div>
                    )}
                </>
            )}

            {(showUploadModal || isClosingUploadModal) && createPortal(
                <div
                    className={`upload-modal-overlay ${isClosingUploadModal ? 'closing' : ''}`}
                    onClick={closeUploadModal}
                >
                    <div className="upload-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="upload-modal-header">
                            <h2>Add Resumes</h2>
                            <button type="button" className="upload-modal-close" onClick={closeUploadModal}>&times;</button>
                        </div>
                        <div className="upload-modal-body">
                            <p className="upload-modal-job-title">
                                Adding to: <strong>{uploadJobTitle}</strong>
                            </p>

                            <div
                                className="upload-dropzone"
                                onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('dragover'); }}
                                onDragLeave={(e) => { e.currentTarget.classList.remove('dragover'); }}
                                onDrop={(e) => {
                                    e.preventDefault();
                                    e.currentTarget.classList.remove('dragover');
                                    const files = Array.from(e.dataTransfer.files);
                                    if (files.length) setUploadFiles(prev => [...prev, ...files]);
                                }}
                                onClick={() => fileInputRef.current?.click()}
                            >
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    multiple
                                    accept=".pdf,.doc,.docx,.txt"
                                    style={{ display: 'none' }}
                                    onChange={handleFileSelect}
                                />
                                <div className="dropzone-content">
                                    <span className="dropzone-icon">📂</span>
                                    <p>Drag & drop resumes here or <span className="dropzone-browse">browse files</span></p>
                                    <small>PDF, DOC, DOCX, TXT — max 30 files</small>
                                </div>
                            </div>

                            {uploadFiles.length > 0 && (
                                <div className="upload-file-list">
                                    {uploadFiles.map((file, idx) => (
                                        <div key={idx} className="upload-file-item">
                                            <span className="upload-file-name">{file.name}</span>
                                            <span className="upload-file-size">{(file.size / 1024).toFixed(0)} KB</span>
                                            <button type="button" className="upload-file-remove" onClick={() => removeFile(idx)}>&times;</button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {uploadResult && (
                                <div className={`upload-result ${uploadResult.success ? 'success' : 'error'}`}>
                                    {uploadResult.message}
                                </div>
                            )}
                        </div>
                        <div className="upload-modal-actions">
                            <button
                                className="btn-confirm-upload"
                                disabled={uploadFiles.length === 0 || isUploading}
                                onClick={handleUploadResumes}
                            >
                                {isUploading ? 'Uploading...' : `Upload ${uploadFiles.length} Resume${uploadFiles.length !== 1 ? 's' : ''}`}
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default JobsListPage;
